import { useTranslation } from 'react-i18next';
import { PERMISSION_MODULES, moduleLabelKey, permissionLabelKey } from '@/shared/permissions/catalog';

export const COMPARE_COLS =
  'grid grid-cols-[minmax(0,1fr)_minmax(5.5rem,1fr)_minmax(5.5rem,1fr)] gap-x-2 md:grid-cols-[minmax(0,1fr)_9.75rem_9.75rem]';

export function RoleCompareHeader({
  thisName,
  otherName,
  pinned = false,
}: {
  thisName: string;
  otherName: string;
  pinned?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`${COMPARE_COLS} items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs md:px-4 md:text-sm dark:border-gray-700 dark:bg-gray-900 ${
        pinned ? 'shadow-sm' : ''
      }`}
    >
      <div role="columnheader" className="font-medium text-gray-600 dark:text-gray-300">
        {t('permissions.modal.comparePermission')}
      </div>
      <div
        role="columnheader"
        className="w-full text-center font-semibold leading-tight text-gray-900 [overflow-wrap:normal] dark:text-white"
      >
        {thisName}
      </div>
      <div
        role="columnheader"
        className="w-full text-center font-semibold leading-tight text-gray-900 [overflow-wrap:normal] dark:text-white"
      >
        {otherName}
      </div>
    </div>
  );
}

export function RoleCompareTable({
  thisName,
  otherName,
  thisKeys,
  otherKeys,
  showHeader = true,
}: {
  thisName: string;
  otherName: string;
  thisKeys: string[];
  otherKeys: string[];
  showHeader?: boolean;
}) {
  const { t } = useTranslation();
  const mine = new Set(thisKeys);
  const other = new Set(otherKeys);

  return (
    <div className="space-y-2.5">
      {showHeader && <RoleCompareHeader thisName={thisName} otherName={otherName} />}
      {PERMISSION_MODULES.map((module) => (
        <section key={module.id} className="rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="px-3 pb-1 pt-2.5 text-sm font-semibold text-teal-700 md:px-4 md:text-base dark:text-teal-300">
            {t(moduleLabelKey(module.id))}
          </h3>
          <ul>
            {module.keys.map((key) => {
              const label = t(permissionLabelKey(key));
              return (
                <li key={key} className={`${COMPARE_COLS} items-start px-3 py-2 md:items-center md:px-4 md:py-2.5`}>
                  <p className="min-w-0 text-sm leading-snug text-gray-800 dark:text-gray-200">{label}</p>
                  <CompareValue on={mine.has(key)} label={label} roleName={thisName} />
                  <CompareValue on={other.has(key)} label={label} roleName={otherName} />
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function CompareValue({ on, label, roleName }: { on: boolean; label: string; roleName: string }) {
  const { t } = useTranslation();
  const text = on ? t('permissions.modal.compareOn') : t('permissions.modal.compareOff');
  const short = on ? t('permissions.modal.compareYes') : t('permissions.modal.compareNo');
  return (
    <span aria-label={`${roleName}: ${label}: ${text}`} className="block w-full text-center">
      <span
        className={
          on
            ? 'inline-flex min-w-[2.25rem] justify-center rounded-md bg-teal-50 px-1.5 py-0.5 text-xs font-semibold text-teal-700 md:hidden dark:bg-teal-950/70 dark:text-teal-200'
            : 'inline-flex min-w-[2.25rem] justify-center rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600 md:hidden dark:bg-gray-700 dark:text-gray-200'
        }
      >
        {short}
      </span>
      <span
        className={
          on
            ? 'hidden w-full text-center text-sm font-medium leading-tight text-teal-700 md:block dark:text-teal-300'
            : 'hidden w-full text-center text-sm leading-tight text-gray-500 md:block dark:text-gray-400'
        }
      >
        {text}
      </span>
    </span>
  );
}
